import { useState, useCallback, useMemo } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";
import DocumentUploadAnalyzer from "../../components/upload/DocumentUploadAnalyzer";
import { DataExtractionPreviewForm } from "../../components/dialog/DataExtractionPreview";
import { Emission } from "../../db_access/emissions";
import { Emission_detail } from "../../db_access/emission_details";
import { GHG_Assessment } from "../../db_access/ghg_assessment";
import {
    M3Button,
    M3IconButton,
    Chip,
    Banner,
} from "../../components/m3";

/**
 * Organization Document Upload Component
 * 
 * Centered modal dialog workflow:
 * 1. Upload & dynamic emission extraction
 * 2. In-place review & verification form
 * 3. Persistence into emissions and emission_details tables using standard ScopeCategory constants
 */
function OrganizationDocumentUpload({
    open = false,
    onClose,
    organizationId,
    organizationType,
    onDataSaved,
}) {
    const { t } = useTranslation();

    const [extractedData, setExtractedData] = useState(null);
    const [ghgPrefill, setGhgPrefill] = useState(null);
    const [analysisStats, setAnalysisStats] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState(null);

    // Field editing in review form
    const handleFieldEdit = useCallback((category, field, value) => {
        setExtractedData((prev) => ({
            ...prev,
            [category]: {
                ...(prev ? prev[category] : {}),
                [field]: value === "" ? null : value,
            },
        }));
    }, []);

    // GHG Assessment field editing in review form
    const handleGhgEdit = useCallback((field, value) => {
        setGhgPrefill((prev) => ({
            ...(prev || {}),
            [field]: value === "" ? null : value,
        }));
    }, []);

    // Handle analysis completion
    const handleAnalysisComplete = useCallback((data, prefill, stats) => {
        setExtractedData(data);
        setGhgPrefill(prefill);
        setAnalysisStats(stats);
    }, []);

    // Reset state and close
    const handleClose = useCallback(() => {
        setExtractedData(null);
        setGhgPrefill(null);
        setAnalysisStats(null);
        setNotification(null);
        if (onClose) onClose();
    }, [onClose]);

    // Save extracted data to database
    const handleSaveData = useCallback(async () => {
        if (!extractedData) return;
        setSaving(true);
        setNotification(null);

        try {
            // Helper to create emission row and associated emission detail row
            const createEmission = async (category, description, gwpValue, isBiogenic = false, mappingDetails = {}) => {
                if (!gwpValue || Number(gwpValue) <= 0) return null;

                const numericGwp = Number(gwpValue);
                const emissionPayload = {
                    supply_chain_id: organizationType === "Supply_chain" ? organizationId : null,
                    structure_id: organizationType === "Structure" ? organizationId : null,
                    corp_id: organizationType === "Corporate" ? organizationId : null,
                    category: category,
                    description: description,
                    gwp: numericGwp,
                    biogenic_gwp: isBiogenic ? numericGwp : 0,
                    is_avoided: 0,
                    is_biogenic: isBiogenic ? 1 : 0,
                };

                const emissionResponse = await Emission.new(emissionPayload);
                const emissionData = await emissionResponse.json();
                const emissionId = emissionData.id;

                await Emission_detail.save([{
                    id: crypto.randomUUID().replace(/-/g, ''),
                    emission_id: emissionId,
                    calculation_method: mappingDetails.calculation_method || 'Average data',
                    type: mappingDetails.type || 'Other',
                    category: category,
                    sub_category: null,
                    description: description,
                    unit: 'tCO2e',
                    quantity: numericGwp,
                    factor: 1.0,
                    uncertainty: 1.0,
                    gwp_unit: 1.0,
                    totalco2eq: numericGwp,
                    biogenic_total: isBiogenic ? numericGwp : 0,
                    is_new: true,
                }]);

                return emissionId;
            };

            // --- 1. Scope 1 Categories (from ScopeCategory.js) ---
            const scope1 = extractedData.scope1 || {};
            const hasScope1Subcategories = [
                scope1.stationary_combustion,
                scope1.mobile_combustion,
                scope1.fugitive_emissions,
                scope1.process_emissions,
            ].some((v) => v !== null && v !== undefined && Number(v) > 0);

            if (hasScope1Subcategories) {
                if (scope1.stationary_combustion && Number(scope1.stationary_combustion) > 0) {
                    await createEmission(
                        "Stationary combustion",
                        "Stationary Combustion (AI extracted)",
                        scope1.stationary_combustion,
                        false,
                        { calculation_method: "Fuel based", type: "Energy sources" }
                    );
                }
                if (scope1.mobile_combustion && Number(scope1.mobile_combustion) > 0) {
                    await createEmission(
                        "Mobile sources",
                        "Mobile Combustion (AI extracted)",
                        scope1.mobile_combustion,
                        false,
                        { calculation_method: "Fuel based", type: "Transport" }
                    );
                }
                if (scope1.fugitive_emissions && Number(scope1.fugitive_emissions) > 0) {
                    await createEmission(
                        "Refrigeration and fire suppression",
                        "Fugitive Emissions (AI extracted)",
                        scope1.fugitive_emissions,
                        false,
                        { calculation_method: "Material balance", type: "Reagent" }
                    );
                }
                if (scope1.process_emissions && Number(scope1.process_emissions) > 0) {
                    await createEmission(
                        "Purchased gases",
                        "Process Emissions (AI extracted)",
                        scope1.process_emissions,
                        false,
                        { calculation_method: "Purchase based", type: "Reagent" }
                    );
                }
            } else if (scope1.total && Number(scope1.total) > 0) {
                await createEmission(
                    "Stationary combustion",
                    "Scope 1 Total (AI extracted)",
                    scope1.total,
                    false,
                    { calculation_method: "Fuel based", type: "Energy sources" }
                );
            }

            // --- 2. Scope 2 Categories (from ScopeCategory.js: Electricity, Steam) ---
            const scope2 = extractedData.scope2 || {};
            const hasScope2Subcategories = [
                scope2.location_based,
                scope2.market_based,
                scope2.purchased_electricity,
                scope2.purchased_heat,
                scope2.purchased_steam,
                scope2.purchased_cooling,
            ].some((v) => v !== null && v !== undefined && Number(v) > 0);

            if (hasScope2Subcategories) {
                if (scope2.location_based && Number(scope2.location_based) > 0) {
                    await createEmission(
                        "Electricity",
                        "Location-Based Electricity (AI extracted)",
                        scope2.location_based,
                        false,
                        { calculation_method: "Location based", type: "Energy sources" }
                    );
                }
                if (scope2.market_based && Number(scope2.market_based) > 0) {
                    await createEmission(
                        "Electricity",
                        "Market-Based Electricity (AI extracted)",
                        scope2.market_based,
                        false,
                        { calculation_method: "Market based", type: "Energy sources" }
                    );
                }
                if (scope2.purchased_electricity && Number(scope2.purchased_electricity) > 0 && !scope2.location_based && !scope2.market_based) {
                    await createEmission(
                        "Electricity",
                        "Purchased Electricity (AI extracted)",
                        scope2.purchased_electricity,
                        false,
                        { calculation_method: "Location based", type: "Energy sources" }
                    );
                }
                if (scope2.purchased_heat && Number(scope2.purchased_heat) > 0) {
                    await createEmission(
                        "Electricity",
                        "Purchased Heat (AI extracted)",
                        scope2.purchased_heat,
                        false,
                        { calculation_method: "Location based", type: "Energy sources" }
                    );
                }
                if (scope2.purchased_steam && Number(scope2.purchased_steam) > 0) {
                    await createEmission(
                        "Steam",
                        "Purchased Steam (AI extracted)",
                        scope2.purchased_steam,
                        false,
                        { calculation_method: "Location based", type: "Energy sources" }
                    );
                }
                if (scope2.purchased_cooling && Number(scope2.purchased_cooling) > 0) {
                    await createEmission(
                        "Electricity",
                        "Purchased Cooling (AI extracted)",
                        scope2.purchased_cooling,
                        false,
                        { calculation_method: "Location based", type: "Energy sources" }
                    );
                }
            } else if (scope2.total && Number(scope2.total) > 0) {
                await createEmission(
                    "Electricity",
                    "Scope 2 Total (AI extracted)",
                    scope2.total,
                    false,
                    { calculation_method: "Location based", type: "Energy sources" }
                );
            }

            // --- 3. Scope 3 Categories (Exact constants with numbers matching ScopeCategory.js) ---
            const scope3 = extractedData.scope3 || {};
            const scope3CategoryMap = {
                cat_1_purchased_goods: { category: "1. Purchased goods and services", desc: "Category 1 — Purchased Goods & Services", method: "Average data", type: "Supply chain" },
                cat_2_capital_goods: { category: "2. Capital goods", desc: "Category 2 — Capital Goods", method: "Average data", type: "Capital goods" },
                cat_3_fuel_energy: { category: "3. Fuel and energy-related activities", desc: "Category 3 — Fuel & Energy Activities", method: "Average data", type: "Energy sources" },
                cat_4_upstream_transport: { category: "4. Upstream transportation and distribution", desc: "Category 4 — Upstream Transportation", method: "Distance based", type: "Transport" },
                cat_5_waste: { category: "5. Waste generated in operations", desc: "Category 5 — Waste in Operations", method: "Waste type specific", type: "Waste Treatment" },
                cat_6_business_travel: { category: "6. Business travel", desc: "Category 6 — Business Travel", method: "Distance based", type: "Transport" },
                cat_7_employee_commuting: { category: "7. Employee commuting", desc: "Category 7 — Employee Commuting", method: "Average data", type: "Transport" },
                cat_8_upstream_leased_assets: { category: "8. Upstream leased assets", desc: "Category 8 — Upstream Leased Assets", method: "Asset specific", type: "Other" },
                cat_9_downstream_transport: { category: "9. Downstream transportation and distribution", desc: "Category 9 — Downstream Transportation", method: "Distance based", type: "Transport" },
                cat_10_processing_sold_products: { category: "10. Processing of sold products", desc: "Category 10 — Processing of Sold Products", method: "Average data", type: "Other" },
                cat_11_use_of_sold_products: { category: "11. Use of sold products", desc: "Category 11 — Use of Sold Products", method: "Direct use-phase", type: "Other" },
                cat_12_end_of_life: { category: "12. End-of-life treatment of sold products", desc: "Category 12 — End-of-Life Treatment", method: "Waste type specific", type: "Waste Treatment" },
                cat_13_downstream_leased_assets: { category: "13. Downstream leased assets", desc: "Category 13 — Downstream Leased Assets", method: "Asset specific", type: "Other" },
                cat_14_franchises: { category: "14. Franchises", desc: "Category 14 — Franchises", method: "Average data", type: "Other" },
                cat_15_investments: { category: "15. Investments", desc: "Category 15 — Investments", method: "Average data", type: "Other" },
            };

            let hasAnyScope3Category = false;
            for (const [key, mapping] of Object.entries(scope3CategoryMap)) {
                const val = scope3[key];
                if (val !== null && val !== undefined && Number(val) > 0) {
                    hasAnyScope3Category = true;
                    await createEmission(
                        mapping.category,
                        `${mapping.desc} (AI extracted)`,
                        val,
                        false,
                        { calculation_method: mapping.method, type: mapping.type }
                    );
                }
            }

            if (!hasAnyScope3Category && scope3.total && Number(scope3.total) > 0) {
                await createEmission(
                    "1. Purchased goods and services",
                    "Scope 3 Total (AI extracted)",
                    scope3.total,
                    false,
                    { calculation_method: "Average data", type: "Supply chain" }
                );
            }

            // --- 4. Biogenic Emissions ---
            const biogenic = extractedData.biogenic || {};
            if (biogenic.total && Number(biogenic.total) > 0) {
                await createEmission(
                    "Stationary combustion",
                    "Biogenic CO₂ Emissions (AI extracted)",
                    biogenic.total,
                    true,
                    { calculation_method: "Fuel based", type: "Energy sources" }
                );
            }

            // --- 5. Update GHG Assessment Metadata ---
            try {
                const normType = (organizationType || "").toLowerCase().replace(/[\s_-]+/g, "");
                let updateGhgFn = null;
                let idFieldName = null;
                let existingGhg = null;

                if (normType === "structure") {
                    idFieldName = "structure_id";
                    updateGhgFn = GHG_Assessment.update_ghg_by_structure;
                    try {
                        const res = await GHG_Assessment.get_by_structure_id(organizationId);
                        existingGhg = await res.json();
                    } catch (e) {
                        console.warn("[GHG_Assessment fetch error]", e);
                    }
                    if (!existingGhg || !existingGhg.id) {
                        try {
                            await GHG_Assessment.new({ structure_id: organizationId });
                            const res = await GHG_Assessment.get_by_structure_id(organizationId);
                            existingGhg = await res.json();
                        } catch (e) {
                            console.warn("[GHG_Assessment init error]", e);
                        }
                    }
                } else if (normType === "supplychain") {
                    idFieldName = "supply_chain_id";
                    updateGhgFn = GHG_Assessment.update_ghg_by_supply_chain;
                    try {
                        const res = await GHG_Assessment.get_by_supply_chain_id(organizationId);
                        existingGhg = await res.json();
                    } catch (e) {
                        console.warn("[GHG_Assessment fetch error]", e);
                    }
                    if (!existingGhg || !existingGhg.id) {
                        try {
                            await GHG_Assessment.new({ supply_chain_id: organizationId });
                            const res = await GHG_Assessment.get_by_supply_chain_id(organizationId);
                            existingGhg = await res.json();
                        } catch (e) {
                            console.warn("[GHG_Assessment init error]", e);
                        }
                    }
                } else if (normType === "corporate") {
                    idFieldName = "corporate_id";
                    updateGhgFn = GHG_Assessment.update_ghg_by_corporate;
                    try {
                        const res = await GHG_Assessment.get_by_corporate_id(organizationId);
                        existingGhg = await res.json();
                    } catch (e) {
                        console.warn("[GHG_Assessment fetch error]", e);
                    }
                    if (!existingGhg || !existingGhg.id) {
                        try {
                            await GHG_Assessment.new({ corporate_id: organizationId });
                            const res = await GHG_Assessment.get_by_corporate_id(organizationId);
                            existingGhg = await res.json();
                        } catch (e) {
                            console.warn("[GHG_Assessment init error]", e);
                        }
                    }
                }

                if (updateGhgFn && idFieldName) {
                    const reportMeta = extractedData.report_metadata || {};
                    const ghgData = ghgPrefill || {};

                    const primaryInfo = ghgData.primary_information || reportMeta.primary_information ||
                        (reportMeta.company_name ? `${reportMeta.company_name} — GHG Assessment (${reportMeta.reporting_year || ''})`.trim() : existingGhg?.primary_information || null);

                    const boundary = ghgData.boundary || reportMeta.organizational_boundary || existingGhg?.boundary || "Operational Control";

                    const rawBaseYear = ghgData.base_year || reportMeta.base_year || reportMeta.reporting_year || existingGhg?.base_year;
                    let parsedBaseYear = null;
                    if (rawBaseYear) {
                        const m = String(rawBaseYear).match(/\b(19\d\d|20\d\d)\b/);
                        parsedBaseYear = m ? parseInt(m[1], 10) : parseInt(rawBaseYear, 10) || null;
                    }

                    const clarification = ghgData.clarification || reportMeta.clarification || existingGhg?.clarification || null;
                    const context = ghgData.context || reportMeta.context || existingGhg?.context || null;
                    const exclusion = ghgData.exclusion || reportMeta.exclusion || existingGhg?.exclusion || null;
                    const assumption = ghgData.assumption || reportMeta.assumption || existingGhg?.assumption || null;

                    const isScope3 = ghgData.is_scope_3_included !== undefined
                        ? (ghgData.is_scope_3_included ? 1 : 0)
                        : (hasAnyScope3Category ? 1 : (existingGhg?.is_scope_3_included ? 1 : 0));

                    const biogenicTotal = extractedData.biogenic?.total ? Number(extractedData.biogenic.total) : (existingGhg?.biogenic_emission ?? null);

                    const ghgPayload = {
                        [idFieldName]: organizationId,
                        primary_information: primaryInfo,
                        boundary: boundary,
                        base_year: parsedBaseYear,
                        clarification: clarification,
                        context: context,
                        exclusion: exclusion,
                        assumption: assumption,
                        is_scope_3_included: isScope3,
                        biogenic_emission: biogenicTotal,
                        functional_unit: existingGhg?.functional_unit || "kgCO2e",
                        other_unit: existingGhg?.other_unit || null,
                    };

                    await updateGhgFn(ghgPayload);
                }
            } catch (ghgErr) {
                console.error("[GHG_Assessment save error]", ghgErr);
            }

            if (onDataSaved) {
                await onDataSaved();
            }

            handleClose();
        } catch (saveErr) {
            console.error("Error saving extracted emission data:", saveErr);
            setNotification({
                tone: "error",
                message: t("Failed to save extracted data: {{message}}", { message: saveErr.message }),
            });
        } finally {
            setSaving(false);
        }
    }, [extractedData, ghgPrefill, organizationId, organizationType, onDataSaved, handleClose, t]);

    const headerChips = useMemo(() => {
        if (!analysisStats || !analysisStats.pages) return null;
        return (
            <Chip tone="primary" size="sm">
                {t("{{count}} Pages Analyzed", { count: analysisStats.pages })}
            </Chip>
        );
    }, [analysisStats, t]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    bgcolor: "var(--m3-surface-container-lowest, #ffffff)",
                    backgroundImage: "none",
                    p: 0,
                    overflow: "hidden",
                    boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
                    border: "1px solid var(--m3-outline-variant)",
                },
            }}
        >
            {/* Dialog Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "20px 24px",
                    borderBottom: "1px solid var(--m3-outline-variant)",
                    background: "var(--m3-surface-container-lowest, #ffffff)",
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                        <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "var(--m3-on-surface)", fontFamily: "var(--m3-font)" }}>
                            {extractedData ? t("Extracted Data Preview") : t("Upload & Analyze Document")}
                        </h2>
                        {extractedData && headerChips}
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", color: "var(--m3-on-surface-variant)", fontFamily: "var(--m3-font)" }}>
                        {extractedData
                            ? t("Review, verify, and edit values before importing into your emission inventory")
                            : t("Upload sustainability reports, BRSR disclosures, or GHG inventories (PDF)")}
                    </p>
                </div>
                <M3IconButton
                    size="sm"
                    icon={<CloseIcon style={{ fontSize: "18px" }} />}
                    variant="standard"
                    onClick={handleClose}
                    tooltip={t("Close")}
                />
            </div>

            {/* Dialog Body */}
            <div style={{ padding: "24px", maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {notification && (
                        <Banner tone={notification.tone} onDismiss={() => setNotification(null)}>
                            {notification.message}
                        </Banner>
                    )}

                    {!extractedData ? (
                        <DocumentUploadAnalyzer
                            organizationId={organizationId}
                            organizationType={organizationType}
                            onAnalysisComplete={handleAnalysisComplete}
                            dataPoints={["scope1", "scope2", "scope3", "energy"]}
                        />
                    ) : (
                        <DataExtractionPreviewForm
                            editedData={extractedData}
                            handleFieldEdit={handleFieldEdit}
                            ghgPrefill={ghgPrefill}
                            handleGhgEdit={handleGhgEdit}
                        />
                    )}
                </div>
            </div>

            {/* Dialog Footer */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 24px",
                    borderTop: "1px solid var(--m3-outline-variant)",
                    background: "var(--m3-surface-container-low, #f8fafc)",
                }}
            >
                <div>
                    {extractedData && (
                        <M3Button
                            variant="text"
                            size="md"
                            onClick={() => setExtractedData(null)}
                            disabled={saving}
                        >
                            {t("Upload another file")}
                        </M3Button>
                    )}
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <M3Button variant="outlined" size="md" onClick={handleClose} disabled={saving}>
                        {t("Cancel")}
                    </M3Button>
                    {extractedData && (
                        <M3Button
                            variant="filled"
                            size="md"
                            icon={<SaveOutlinedIcon style={{ fontSize: "16px" }} />}
                            onClick={handleSaveData}
                            loading={saving}
                        >
                            {saving ? t("Saving Data...") : t("Save Data to Inventory")}
                        </M3Button>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

export default OrganizationDocumentUpload;
